import React, {useCallback, useEffect, useState} from 'react';
import axios from 'axios';
import {Container, Col, Row} from 'reactstrap';
import {usePhaseState, usePhaseDispatch} from 'funnel-schedule/phase-context';
import CountdownTimer from 'components/Elements/CountdownTimer';
import WebinarVideo from 'components/WebinarVideo';
import Video from 'components/Video';
import {useUserState} from 'contexts/user';
import MasterclassReregisterForm from 'components/MasterclassReregisterForm';
import masterclass from 'pages/masterclass';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

const {SF_API_URL} = process.env;
const {SITE_ID} = process.env;

export default function Webinar({context = 'webinar', noun = 'Webinar', headingSection, footerSection, ...props}) {
  const {registrationId, bucket} = useUserState();
  const phases = usePhaseState().phases || {};
  const dispatch = usePhaseDispatch();
  const schedule = phases[context] || {};
  const {attended, events, phase, pos, time, url: webinar_url, id: webinar_id} = schedule;

  useEffect(() => {
    if (phase !== 'during' || attended || !(webinar_id && registrationId && time)) return;
    axios.post(`${SF_API_URL}/webinar/${webinar_id}/event/`, {
      type: 'attend',
      registrationId,
      time
    });
    dispatch({
      type: 'UPDATE_PHASE',
      payload: {
        context,
        data: {
          attended: true
        }
      }
    });
  }, [schedule, registrationId]);

  useEffect(() => {
    pos < 0 &&
      setTimeout(
        () =>
          registrationId &&
          axios
            .get(`${SF_API_URL}/site/${SITE_ID}/state/`, {
              params: {
                registrationId
              }
            })
            .then(({data}) =>
              dispatch({
                type: 'SET_PHASE',
                payload: {...data, pos: 0}
              })
            ),
        pos * -1000
      );
  }, [pos, registrationId]);

  const onWebinarEnd = useCallback(
    (e) => {
      dispatch({
        type: 'UPDATE_PHASE',
        payload: {
          context,
          data: {
            phase: 'replay',
            pos: 0
          }
        }
      });
    },
    [schedule]
  );

  const onEventFired = useCallback(
    (name) => {
      if ((schedule.firedEvents || []).find((e) => e === name)) return;
      axios.post(`${SF_API_URL}/webinar/${webinar_id}/event/`, {
        type: 'custom',
        name,
        registrationId,
        time
      });
      dispatch({
        type: 'ADD_PHASE_EVENT',
        payload: {
          context,
          event: name
        }
      });
    },
    [schedule]
  );

  switch (phase) {
    case 'before':
      return (
        <>
          {headingSection}
          <Row className="d-flex justify-content-center mt-5">
            <Col lg="10" className="text-center">
              <h3 className="section-heading">Your {noun} starts in:</h3>
              <CountdownTimer className="countdown-dark" time={pos * -1000 + new Date().getTime()} />
            </Col>
          </Row>
          {footerSection}
        </>
      );
    case 'during':
      return (
        <>
          {headingSection}
          <Row className="d-flex justify-content-center">
            <Col lg="10" className="video-col px-0 px-sm-3">
              <WebinarVideo
                {...schedule}
                url={webinar_url}
                onEnded={onWebinarEnd}
                onEventFired={onEventFired}
                noun={noun}
                controls={false}
                events={events}
                label="HHC - Masterclass"
                defaultTextTrack="en"
              />
            </Col>
          </Row>
          {footerSection}
        </>
      );
    case 'replay':
    case 'after':
      return (
        <>
          {headingSection}
          <Row className="d-flex justify-content-center">
            <Col lg="10" className="video-col px-0 px-sm-3">
              <div className="px-3 px-sm-0">
                <h2 className="section-heading text-center">Your {noun} is now on replay.</h2>
              </div>
              <Video url={webinar_url} events={events} label="HHC - Masterclass" />
            </Col>
          </Row>
          {footerSection}
        </>
      );
    case 'missed':
      return (
        <>
          {headingSection}
          <Row className="d-flex justify-content-center mt-5">
            <Col lg="10">
              <h2 className="section-heading text-center">
                Your {noun} has ended <br className="d-none d-md-block" />
                but you can book <br className="d-none d-sm-block d-md-none" />a new time now!
              </h2>
              <MasterclassReregisterForm />
            </Col>
          </Row>
          {footerSection}
        </>
      );
    default:
      return null;
  }
}
